/**
 * Per-display detection + UI scale for BUDB PKR.
 * Drives portrait table layout, Apple PWA chrome, and CSS --ui-scale.
 */
import { useEffect, useState } from "react";

export type DisplayBreakpoint = "xs" | "sm" | "md" | "lg" | "xl";

export interface DisplayInfo {
  width: number;
  height: number;
  dpr: number;
  orientation: "portrait" | "landscape";
  breakpoint: DisplayBreakpoint;
  /** 0.78–1.12 — seat/card chrome scale */
  uiScale: number;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** Narrow + portrait → vertical oval table layout */
  useVerticalTable: boolean;
  /** iOS Safari / standalone home-screen */
  isStandalone: boolean;
  isIos: boolean;
  isTouch: boolean;
  safeTop: number;
  safeBottom: number;
}

function readSafe(inset: "top" | "bottom"): number {
  if (typeof window === "undefined" || !window.getComputedStyle) return 0;
  const probe = document.documentElement;
  const val = getComputedStyle(probe).getPropertyValue(
    inset === "top" ? "--sat" : "--sab",
  );
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : 0;
}

function compute(): DisplayInfo {
  const width = typeof window !== "undefined" ? window.innerWidth : 1024;
  const height = typeof window !== "undefined" ? window.innerHeight : 768;
  const dpr =
    typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 3) : 1;
  const orientation: "portrait" | "landscape" =
    height >= width ? "portrait" : "landscape";

  let breakpoint: DisplayBreakpoint = "xl";
  if (width < 380) breakpoint = "xs";
  else if (width < 640) breakpoint = "sm";
  else if (width < 900) breakpoint = "md";
  else if (width < 1200) breakpoint = "lg";

  const isPhone = width < 640;
  const isTablet = width >= 640 && width < 1024;
  const isDesktop = width >= 1024;

  // Scale: phones shrink chrome; large desktops slightly enlarge
  let uiScale = 1;
  if (width < 360) uiScale = 0.78;
  else if (width < 400) uiScale = 0.84;
  else if (width < 480) uiScale = 0.9;
  else if (width < 640) uiScale = 0.94;
  else if (width >= 1440) uiScale = 1.08;
  else if (width >= 1200) uiScale = 1.04;

  // Vertical table: phone portrait, or short wide phones in portrait-ish height
  const useVerticalTable =
    orientation === "portrait" && (isPhone || (width < 900 && height > width));

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      // iOS Safari "Add to Home Screen"
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone));

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (typeof navigator !== "undefined" &&
      navigator.platform === "MacIntel" &&
      navigator.maxTouchPoints > 1);

  const isTouch =
    typeof window !== "undefined" &&
    (window.matchMedia("(pointer: coarse)").matches ||
      navigator.maxTouchPoints > 0);

  return {
    width,
    height,
    dpr,
    orientation,
    breakpoint,
    uiScale,
    isPhone,
    isTablet,
    isDesktop,
    useVerticalTable,
    isStandalone,
    isIos,
    isTouch,
    safeTop: readSafe("top"),
    safeBottom: readSafe("bottom"),
  };
}

function applyCssVars(info: DisplayInfo) {
  const root = document.documentElement;
  root.style.setProperty("--ui-scale", String(info.uiScale));
  root.style.setProperty("--display-w", `${info.width}px`);
  root.style.setProperty("--display-h", `${info.height}px`);
  root.dataset.orientation = info.orientation;
  root.dataset.breakpoint = info.breakpoint;
  root.dataset.verticalTable = info.useVerticalTable ? "1" : "0";
  root.dataset.standalone = info.isStandalone ? "1" : "0";
  root.dataset.ios = info.isIos ? "1" : "0";
  root.classList.toggle("display-portrait", info.orientation === "portrait");
  root.classList.toggle("display-landscape", info.orientation === "landscape");
  root.classList.toggle("display-vertical-table", info.useVerticalTable);
  root.classList.toggle("display-phone", info.isPhone);
  root.classList.toggle("display-standalone", info.isStandalone);
}

const defaultInfo: DisplayInfo = {
  width: 1024,
  height: 768,
  dpr: 1,
  orientation: "landscape",
  breakpoint: "lg",
  uiScale: 1,
  isPhone: false,
  isTablet: false,
  isDesktop: true,
  useVerticalTable: false,
  isStandalone: false,
  isIos: false,
  isTouch: false,
  safeTop: 0,
  safeBottom: 0,
};

/** Full display snapshot — updates on resize / orientation / DPR. */
export function useDisplay(): DisplayInfo {
  const [info, setInfo] = useState<DisplayInfo>(() =>
    typeof window === "undefined" ? defaultInfo : compute(),
  );

  useEffect(() => {
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = compute();
        applyCssVars(next);
        setInfo(next);
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    const mq = window.matchMedia("(display-mode: standalone)");
    mq.addEventListener?.("change", update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      mq.removeEventListener?.("change", update);
    };
  }, []);

  return info;
}

/** Compact boolean — same breakpoint as before (< 768). */
export function useIsMobile() {
  const d = useDisplay();
  return d.width < 768;
}
